import { StatusIds } from 'services/api';
import { ParsedService } from 'services/types';

const DATE_FIELD_CANDIDATES = [
  'handoverDate',
  'handover_date',
  'handoverTimestamp',
  'handover_timestamp',
  'completedAt',
  'completed_at',
  'pickedUpAt',
  'picked_up_at',
  'dateClosed',
  'date_closed',
  'readyToPickupAt',
  'ready_to_pickup_at',
];

const ASSOCIATE_NAME_FIELDS = [
  'associateName',
  'associate_name',
  'associate',
  'associateFullName',
  'associate_full_name',
  'createdBy',
  'created_by',
];

const ASSOCIATE_ID_FIELDS = [
  'associateId',
  'associate_id',
  'associateNumber',
  'associate_number',
  'associateCode',
  'associate_code',
];

const ASSOCIATE_EMAIL_FIELDS = [
  'associateEmail',
  'associate_email',
];

const pickFirstString = (sources: Array<Record<string, any> | undefined>, keys: string[]) => {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }
  return undefined;
};

const parseDateValue = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value > 1e12) return value;
    return Math.round(value * 1000); // assume seconds
  }
  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      if (value.trim().length >= 13) return numeric;
      if (numeric > 1e12) return numeric;
      if (numeric > 1e9) return Math.round(numeric);
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }
  return null;
};

export const formatOrderDateTime = (
  value?: number | string | Date | null,
  opts?: { timeZone?: string }
) => {
  const timestamp = value instanceof Date ? value.getTime() : parseDateValue(value ?? null);
  if (!timestamp) return '—';
  try {
    const formatterOptions: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    if (opts?.timeZone) {
      formatterOptions.timeZone = opts.timeZone;
    }
    const formatter = new Intl.DateTimeFormat('en-US', formatterOptions);
    return formatter.format(new Date(timestamp));
  } catch {
    return typeof value === 'string' ? value : '—';
  }
};

export const parseServiceList = (raw?: ParsedService[] | string | null | unknown): ParsedService[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as ParsedService[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as ParsedService[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const serviceStatusIsCompleted = (service: ParsedService) => {
  const rawStatus = (service as any)?.status ?? (service as any)?.status_id ?? (service as any)?.statusId;
  if (rawStatus == null) return false;
  const numeric = Number(rawStatus);
  if (Number.isFinite(numeric)) {
    return numeric === StatusIds.COMPLETED;
  }
  return String(rawStatus).trim().toUpperCase() === 'COMPLETED';
};

export const deriveHandoverTimestamp = (
  entity?: Record<string, any> | null,
  servicesOverride?: ParsedService[] | string | null
): number | null => {
  const directCandidate = entity
    ? DATE_FIELD_CANDIDATES
      .map((field) => parseDateValue(entity[field]))
      .find((value) => value != null)
    : null;
  if (directCandidate) return directCandidate;

  const services = servicesOverride !== undefined
    ? parseServiceList(servicesOverride)
    : parseServiceList(entity?.services);

    if (
    entity &&
    services?.length === 0 &&
    serviceStatusIsCompleted(entity as any)
  ) {
    const serviceUpdated = parseDateValue(entity.serviceUpdated);
    if (serviceUpdated != null) {
      return serviceUpdated;
    }
  }

  if (!services.length) return null;

  const completedDates = services
    .filter((svc) => svc && serviceStatusIsCompleted(svc))
    .map((svc) => parseDateValue((svc as any)?.date_closed ?? (svc as any)?.dateClosed ?? (svc as any)?.date_updated ?? (svc as any)?.dateUpdated))
    .filter((value): value is number => Boolean(value));

  if (!completedDates.length) return null;
  return Math.max(...completedDates);
};

export interface AssociateInfo {
  name?: string | null;
  id?: string | null;
  email?: string | null;
}

export const extractAssociateInfo = (entity?: Record<string, any> | null): AssociateInfo => {
  if (!entity) return {};
  const sources = [entity, (entity as any)?.meta, (entity as any)?.metadata];
  let name = pickFirstString(sources, ASSOCIATE_NAME_FIELDS);
  if (!name) {
    const first = pickFirstString(sources, ['associate_first_name']);
    const last = pickFirstString(sources, ['associate_last_name']);
    const combined = [first, last].filter(Boolean).join(' ').trim();
    if (combined) {
      name = combined;
    }
  }
  const id = pickFirstString(sources, ASSOCIATE_ID_FIELDS);
  const email = pickFirstString(sources, ASSOCIATE_EMAIL_FIELDS);
  return { name, id, email };
};
